import {
  PortalEventService,
  PortalEventServiceGetTicketsBonusMediaRequest,
} from "@spwn-portal/grpc";
import { useEffect, useMemo, useState } from "react";
import { useConnectClient } from "utility/connectWeb";

import type { Store } from "store";

type Input = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
};

export const useTicketBonusMap = (input: Input) => {
  const { transaction } = input;
  const connectClient = useConnectClient(PortalEventService);
  const [bonusMap, setBonusMap] = useState<Record<string, boolean> | null>(
    null
  );
  const [isLoadingBonus, setIsLoadingBonus] = useState(false);

  const productsByEvent = useMemo(() => {
    if (transaction?.products === null || transaction?.products === undefined)
      return null;

    return transaction.products.reduce(
      (acc, product) => {
        if (product.productType !== "ticket") return acc;

        const eventId = product.eid;
        if (!acc[eventId]) acc[eventId] = [];
        acc[eventId].push(product.ref.id);
        return acc;
      },
      {} as Record<string, string[]>
    );
  }, [transaction]);

  useEffect(() => {
    const fetchBonusData = async () => {
      if (productsByEvent === null) {
        setBonusMap(null);
        return;
      }

      const eventIds = Object.keys(productsByEvent);
      if (eventIds.length === 0) {
        setBonusMap(null);
        return;
      }

      setIsLoadingBonus(true);
      try {
        const results = await Promise.allSettled(
          eventIds.map(async (eventId) =>
            connectClient.getTicketsBonusMedia(
              new PortalEventServiceGetTicketsBonusMediaRequest({
                eventId,
                ticketIds: productsByEvent[eventId],
              })
            )
          )
        );

        const resultMap: Record<string, boolean> = {};
        results.forEach((result) => {
          if (result.status === "fulfilled" && result.value.ticketBonusMedia) {
            result.value.ticketBonusMedia.forEach((item) => {
              resultMap[item.ticketId] = true;
            });
          }
        });
        setBonusMap(resultMap);
      } finally {
        setIsLoadingBonus(false);
      }
    };

    void fetchBonusData();
  }, [productsByEvent, connectClient]);

  return { bonusMap, isLoadingBonus };
};
